export const EMPTY_OBJECT = Object.freeze({});

// Constants
export const LIFE_CYCLE_EVENT_NAME_ADD_TO_BUNDLE = 'grouped-promotion-products.add-to-bundle';

// TODO: Remove this utility when Grouped Promotions Component is fully integrated with Cart
// Utility to generate an empty cart model response
export const getEmptyCart = () => Object.freeze({
  CartModel: Object.freeze({
    expressCheckout: false,
    paypalExpress: false,
    messagesModel: Object.freeze([
      Object.freeze({ shortDescription: 'Empty Shopping Cart', correlationType: 'generic' })
    ]),
    coProceed: false,
    atcCheckoutErrFlow: false,
    system: 'aurora'
  })
});

/**
 * Get the promotion group data for a specific anchor item
 * @param {Object} groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} anchorItemId - The anchor item ID
 * @returns {Object|null} The group data or null if not found
 */
export const getGroupDataByAnchor = (groupedItemsByAnchor, anchorItemId) => {
  if (!groupedItemsByAnchor || !anchorItemId) {
    return null;
  }
  return groupedItemsByAnchor[anchorItemId] || null;
};

/**
 * Get the promotionItemsFromProps for a specific anchor item
 * These are the items passed in by the consumer (from cart, etc.)
 * @param {Object} groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} anchorItemId - The anchor item ID
 * @returns {Array} Array of promotion items from props, or empty array
 */
export const getPromotionItemsFromProps = (groupedItemsByAnchor, anchorItemId) => {
  const groupData = getGroupDataByAnchor(groupedItemsByAnchor, anchorItemId);
  return groupData?.promotionItemsFromProps || [];
};

/**
 * Check if an itemId exists in the promotionItemsFromProps for a specific anchor
 * @param {Object} groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} anchorItemId - The anchor item ID
 * @param {string} itemId - The item ID to check
 * @returns {boolean} True if the item exists in promotionItemsFromProps
 */
export const isItemInPromotionItemsFromProps = (groupedItemsByAnchor, anchorItemId, itemId) => {
  const promotionItems = getPromotionItemsFromProps(groupedItemsByAnchor, anchorItemId);
  return promotionItems.some((item) => item.itemId === itemId);
};

/**
 * Get array of itemIds from promotionItemsFromProps for a specific anchor
 * @param {Object} groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} anchorItemId - The anchor item ID
 * @returns {Array<string>} Array of item IDs
 */
export const getPromotionItemIds = (groupedItemsByAnchor, anchorItemId) => {
  const promotionItems = getPromotionItemsFromProps(groupedItemsByAnchor, anchorItemId);
  return Object.freeze(promotionItems.map((item) => item.itemId));
};

/**
 * Calculate cart value and quantity for grouped items
 * @param {Object} params - Parameters object
 * @param {Object} params.groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} params.anchorItemId - The anchor item ID
 * @param {Map} params.allListProductsAndAnchor - Map of all products and anchor items
 * @returns {Object} Object with cartValue and cartQuantity
 */
export const calculateGroupedItemsCartValues = ({
  groupedItemsByAnchor,
  anchorItemId,
  allListProductsAndAnchor,
}) => {
  let cartValue = 0;
  let cartQuantity = 0;

  if (
    !groupedItemsByAnchor
    || !groupedItemsByAnchor[anchorItemId]
    || !allListProductsAndAnchor
  ) {
    return Object.freeze({ cartValue, cartQuantity });
  }

  const groupedItems = groupedItemsByAnchor[anchorItemId].promotionItemsFromProps;

  if (!groupedItems.length) {
    return Object.freeze({ cartValue, cartQuantity });
  }

  groupedItems.forEach((groupedProduct) => {
    const productData = allListProductsAndAnchor.get(groupedProduct.itemId);
    const itemPrice = Number(productData?.pricing?.value) || 0;
    cartValue += (groupedProduct.quantity * itemPrice);
    cartQuantity += groupedProduct.quantity;
  });

  return Object.freeze({ cartValue, cartQuantity });
};

/**
 * Map promotionItemsFromProps itemIds to full product data
 * @param {Object} params - Parameters object
 * @param {Array} params.promotionItemsFromProps - Array of promotion items from props
 * @param {Map} params.allListProductsAndAnchor - Map of all products and anchor items
 * @returns {Array} Array of full product objects
 */
export const mapPromotionItemsToProducts = ({
  promotionItemsFromProps,
  allListProductsAndAnchor,
}) => {
  if (!promotionItemsFromProps?.length || !allListProductsAndAnchor) {
    return Object.freeze([]);
  }

  const products = promotionItemsFromProps
    .map(({ itemId }) => allListProductsAndAnchor.get(itemId))
    .filter(Boolean); // Remove any undefined entries

  return Object.freeze(products);
};

/**
 * Check if pre-selection should be skipped in a grouped context
 * @param {Object} params - Parameters object
 * @param {boolean} params.isGroupedContext - Whether we're in a grouped context
 * @param {string} params.currentActiveGroup - Currently active/expanded group
 * @param {string} params.anchorItemId - The anchor item ID
 * @returns {boolean} True if pre-selection should be skipped
 */
export const shouldSkipPreselection = ({
  isGroupedContext,
  currentActiveGroup,
  anchorItemId,
}) => {
  // In grouped context, only pre-select when this group is active
  return isGroupedContext && currentActiveGroup !== anchorItemId;
};

/**
 * Trigger the add-to-bundle lifecycle event
 * @param {Object} params - Parameters object
 * @param {Array<string>} params.itemIds - Array of item IDs to add to bundle
 * @param {Map} params.allListProductsAndAnchor - Map of all products and anchor items
 * @param {number} params.promotionId - The promotion ID
 * @param {string} params.promotionTag - The promotion experience tag
 */
export const triggerAddToBundleEvent = ({
  itemIds,
  allListProductsAndAnchor,
  promotionId,
  promotionTag,
}) => {
  if (!window.LIFE_CYCLE_EVENT_BUS) {
    // LIFE_CYCLE_EVENT_BUS not available
    return;
  }

  const addToBundleSelections = itemIds.map(
    (itemId) => ({
      ...allListProductsAndAnchor.get(itemId),
      promotionId,
      promotionTag
    })
  );

  window.LIFE_CYCLE_EVENT_BUS.trigger(
    LIFE_CYCLE_EVENT_NAME_ADD_TO_BUNDLE,
    () => addToBundleSelections
  );
};

/**
 * Check if the add to bundle button should be disabled
 * @param {Object} params - Parameters object
 * @param {Array<string>} params.itemIds - Array of selected item IDs
 * @param {Object} params.groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} params.currentActiveGroup - Currently active/expanded group
 * @returns {boolean} True if the button should be disabled
 */
export const isAddToBundleDisabled = ({
  itemIds,
  groupedItemsByAnchor,
  currentActiveGroup,
}) => {
  if (!groupedItemsByAnchor || !currentActiveGroup) {
    return false;
  }

  const groupData = groupedItemsByAnchor[currentActiveGroup];
  if (!groupData?.promotionItemsFromProps) {
    return false;
  }

  return itemIds.length === groupData.promotionItemsFromProps.length;
};

/**
 * Extract eligibility criteria from promotion data
 * @param {Object} promotionData - The promotion data object
 * @returns {Object} Object with srcEligibility and tgtEligibility
 */
export const extractEligibilityCriteria = (promotionData) => {
  if (!promotionData?.eligibilityCriteria) {
    return Object.freeze({ srcEligibility: null, tgtEligibility: null });
  }

  const srcEligibility = promotionData.eligibilityCriteria.find(
    (criterion) => criterion.itemGroup === 'SRC-1'
  );
  const tgtEligibility = promotionData.eligibilityCriteria.find(
    (criterion) => criterion.itemGroup === 'TGT-1'
  );

  return Object.freeze({ srcEligibility, tgtEligibility });
};

/**
 * Combine cart itemIds with promotion itemIds for SRC and TGT lists
 * All cart items are added to SRC only, TGT remains unchanged
 * @param {Object} params - Parameters object
 * @param {Array<string>} params.cartItemIds - Array of item IDs from cart
 * @param {Array<string>} params.rawSrcItemIds - Original SRC itemIds from promotion
 * @param {Array<string>} params.rawTgtItemIds - Original TGT itemIds from promotion
 * @returns {Object} Object with combinedSrcItemIds and combinedTgtItemIds
 */
export const combineCartItemsWithPromotion = ({
  cartItemIds = [],
  rawSrcItemIds = [],
  rawTgtItemIds = [],
}) => {
  const cartItemIdsSet = new Set(cartItemIds);

  // Combine cart itemIds with raw promotion itemIds, prioritizing cart items
  // All cart items are added to SRC only
  const combinedSrcItemIds = [
    ...cartItemIds,
    ...rawSrcItemIds.filter((id) => !cartItemIdsSet.has(id))
  ];

  // TGT list remains unchanged from raw data
  const combinedTgtItemIds = rawTgtItemIds;

  return Object.freeze({ combinedSrcItemIds, combinedTgtItemIds });
};

/**
 * Enrich promotion data with cart items by updating eligibility criteria itemIds
 * @param {Object} params - Parameters object
 * @param {Object} params.data - The original promotion data
 * @param {Array} params.promotionItemsFromProps - Items from props (cart items)
 * @returns {Object} Enriched promotion data with updated itemIds
 */
export const enrichPromotionDataWithCartItems = ({
  data,
  promotionItemsFromProps = [],
}) => {
  if (!data || !promotionItemsFromProps.length) {
    return data;
  }

  const promotionData = data?.promotionProducts?.promotions?.[0];
  if (!promotionData) {
    return data;
  }

  // Extract eligibility criteria
  const { srcEligibility, tgtEligibility } = extractEligibilityCriteria(promotionData);

  // Get itemIds from the raw promotion data
  const rawSrcItemIds = srcEligibility?.itemIds || [];
  const rawTgtItemIds = tgtEligibility?.itemIds || [];

  // Extract itemIds from cart items
  const cartItemIds = promotionItemsFromProps.map((item) => item.itemId);

  // Combine cart itemIds with promotion itemIds
  const { combinedSrcItemIds, combinedTgtItemIds } = combineCartItemsWithPromotion({
    cartItemIds,
    rawSrcItemIds,
    rawTgtItemIds,
  });

  // Update the promotion data with combined itemIds
  const mutatedPromotionData = {
    ...promotionData,
    eligibilityCriteria: promotionData.eligibilityCriteria?.map((criterion) => {
      if (criterion.itemGroup === 'SRC-1') {
        return Object.freeze({
          ...criterion,
          itemIds: combinedSrcItemIds
        });
      }
      if (criterion.itemGroup === 'TGT-1') {
        return Object.freeze({
          ...criterion,
          itemIds: combinedTgtItemIds
        });
      }
      return criterion;
    })
  };

  return Object.freeze({
    ...data,
    promotionProducts: Object.freeze({
      ...data.promotionProducts,
      promotions: Object.freeze([mutatedPromotionData])
    })
  });
};

/**
 * Determine the anchor itemId for a promotion
 * Uses existing anchor if still in the list, otherwise uses first item
 * @param {Object} params - Parameters object
 * @param {Array} params.itemsList - Array of items in the promotion group
 * @param {string} params.currentAnchor - Current anchor itemId (if any)
 * @returns {string|null} The anchor itemId or null if no valid items
 */
export const determineAnchorItemId = ({
  itemsList = [],
  currentAnchor = null,
}) => {
  if (!itemsList.length || !itemsList[0]?.itemId) {
    return null;
  }

  const itemIdsInList = new Set(itemsList.map((item) => item.itemId));

  // Use existing anchor if it's still in the list, otherwise use first item
  return currentAnchor && itemIdsInList.has(currentAnchor)
    ? currentAnchor
    : itemsList[0].itemId;
};

/**
 * Create promotion groups from items dictionary
 * Input: { promotionId: [{itemId, quantity}, ...], ... }
 * Output: [{ promotionId, anchorItemId, allGroupItemsFromProps: [...] }, ...]
 * @param {Object} params - Parameters object
 * @param {Object} params.items - Dictionary of promotionId to items array
 * @param {Object} params.anchorsByPromotion - Map of promotionId to current anchor itemId
 * @returns {Array} Array of promotion group objects
 */
export const createPromotionGroups = ({
  items = {},
  anchorsByPromotion = {},
}) => {
  const groups = [];

  Object.entries(items).forEach(([promotionId, itemsList]) => {
    if (!itemsList.length || !itemsList[0]?.itemId) {
      return;
    }

    const currentAnchor = anchorsByPromotion[promotionId];
    const anchorItemId = determineAnchorItemId({
      itemsList,
      currentAnchor,
    });

    if (anchorItemId) {
      groups.push(Object.freeze({
        promotionId,
        anchorItemId,
        allGroupItemsFromProps: itemsList
      }));
    }
  });

  return Object.freeze(groups);
};

/**
 * Transform grouped promotion products array into dictionary keyed by itemId
 * @param {Array} groupedPromotionProducts - Array of grouped promotion products
 * @returns {Object} Dictionary keyed by anchor itemId
 */
export const transformToGroupedDictionary = (groupedPromotionProducts = []) => {
  const groupedPromoItemsByAnchor = {};

  groupedPromotionProducts.forEach((group) => {
    if (group.itemId) {
      groupedPromoItemsByAnchor[group.itemId] = group;
    }
  });

  return Object.freeze(groupedPromoItemsByAnchor);
};

/**
 * Check if an item should be disabled/locked in a grouped promotion context
 * Items are locked if they're part of the currently active group's promotionItemsFromProps
 * This prevents users from selecting/deselecting items that are already in the cart/bundle
 * @param {Object} params - Parameters object
 * @param {string} params.itemId - The item ID to check
 * @param {Object} params.groupedItemsByAnchor - Dictionary of grouped items keyed by anchor itemId
 * @param {string} params.currentActiveGroup - Currently active/expanded group
 * @returns {boolean} True if the item should be disabled/locked
 */
export const shouldDisableGroupedItem = ({
  itemId,
  groupedItemsByAnchor,
  currentActiveGroup,
}) => {
  if (!currentActiveGroup || !groupedItemsByAnchor) {
    return false;
  }

  const groupData = groupedItemsByAnchor[currentActiveGroup];
  if (!groupData?.promotionItemsFromProps) {
    return false;
  }

  return groupData.promotionItemsFromProps.some(
    ({ itemId: groupedItemId }) => groupedItemId === itemId
  );
};
